/** @jsxImportSource ../../i18n */
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CornerDownLeft, Eye, EyeOff, FileText, LogOut, MessageCircle, Radio, RefreshCw, Search, Users } from 'lucide-react';
import { logout } from '../../store/slices/authSlice';
import { fetchPortfolio, fetchWatchlist } from '../../store/slices/portfolioSlice';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { useToast } from '../../contexts/ToastContext';
import { cx } from '../../theme/cx';
import { Dialog, IconTile, Kbd, SymbolMark } from '../ui';
import { NAV_ITEMS } from './navConfig';
import { tr } from '../../i18n';

export const normalize = (text) => String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

const scoreField = (query, field, weight) => {
  const value = normalize(field);
  if (!value) return 0;
  if (value === query) return 4 * weight;
  if (value.startsWith(query)) return 3 * weight;
  if (value.split(/[\s:/'’()-]+/).some((word) => word.startsWith(query))) return 2 * weight;
  return value.includes(query) ? weight : 0;
};

// The label and hint are also matched as displayed (src/i18n), so an
// English reader finds a page by its English name.
export const scoreCommand = (query, command) => Math.max(
  scoreField(query, command.label, 1),
  scoreField(query, tr(command.label), 1),
  scoreField(query, command.hint, 0.6),
  scoreField(query, tr(command.hint), 0.6),
  ...(command.keywords || []).map((k) => scoreField(query, k, 0.5)),
);

const GROUP_LIMIT = 8;

export const CommandPalette = ({ open, onClose, onOpenChat }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const { hidden, toggle } = usePrivacy();
  const { positions, watchlist, watchlistLoading, summary, loading, saxoConnected } = useSelector((state) => state.portfolio);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef(null);
  const requested = useRef(false);
  const listId = useId();

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    if (requested.current) return;
    requested.current = true;
    if (!watchlist.length && !watchlistLoading) dispatch(fetchWatchlist());
    if (!summary && !loading && saxoConnected) dispatch(fetchPortfolio());
  }, [open, dispatch, watchlist.length, watchlistLoading, summary, loading, saxoConnected]);

  const commands = useMemo(() => {
    const go = (to) => () => navigate(to);
    const instrument = (group, item) => ({
      id: `${group}:${item.symbol}`,
      group,
      label: item.symbol,
      hint: item.description,
      mark: item.symbol,
      run: go(`/stock/${encodeURIComponent(item.symbol)}`),
    });
    return [
      ...NAV_ITEMS.map((item) => ({ id: `page:${item.to}`, group: 'Pages', label: item.label, hint: item.section, icon: item.icon, keywords: item.keywords, run: go(item.to) })),
      ...positions.map((p) => instrument('Portefeuille', p)),
      ...watchlist.map((w) => instrument('Watchlist', w)),
      { id: 'action:privacy', group: 'Actions', label: hidden ? 'Afficher les montants' : 'Masquer les montants', icon: hidden ? Eye : EyeOff, keywords: ['mode discret', 'confidentialite', 'privacy'], run: toggle },
      { id: 'action:chat', group: 'Actions', label: 'Parler à Gladys', hint: 'Assistante IA', icon: MessageCircle, keywords: ['chat', 'assistante', 'question'], run: onOpenChat },
      {
        id: 'action:refresh',
        group: 'Actions',
        label: 'Actualiser le portefeuille',
        icon: RefreshCw,
        keywords: ['refresh', 'synchroniser', 'mettre a jour'],
        run: async () => {
          const result = await dispatch(fetchPortfolio());
          if (fetchPortfolio.fulfilled.match(result)) toast.success('Portefeuille actualisé');
          else toast.error('Impossible d’actualiser le portefeuille', { description: result.payload || undefined });
        },
      },
      { id: 'action:experts', group: 'Actions', label: 'Configurer les experts', hint: 'Réglages', icon: Users, keywords: ['panel', 'experts', 'rapporteur'], run: go('/settings?tab=experts') },
      { id: 'action:report', group: 'Actions', label: 'Lire le dernier rapport', hint: 'Rapports quotidiens', icon: FileText, keywords: ['rapport', 'matinal'], run: go('/reports') },
      { id: 'action:live', group: 'Actions', label: 'Suivre le débat en direct', hint: 'Rapports quotidiens', icon: Radio, keywords: ['live', 'debat', 'direct'], run: go('/reports/live') },
      {
        id: 'action:logout',
        group: 'Actions',
        label: 'Se déconnecter',
        icon: LogOut,
        keywords: ['logout', 'deconnexion', 'quitter'],
        run: () => {
          dispatch(logout());
          navigate('/login');
        },
      },
    ];
  }, [positions, watchlist, hidden, toggle, onOpenChat, dispatch, navigate, toast]);

  const groups = useMemo(() => {
    const q = normalize(query);
    const visible = q
      ? commands.map((c) => ({ ...c, score: scoreCommand(q, c) })).filter((c) => c.score > 0).sort((a, b) => b.score - a.score)
      : commands.filter((c) => c.group === 'Pages' || c.group === 'Actions');
    const order = ['Pages', 'Portefeuille', 'Watchlist', 'Actions'];
    return order
      .map((label) => ({ label, items: visible.filter((c) => c.group === label).slice(0, q ? GROUP_LIMIT : undefined) }))
      .filter((g) => g.items.length);
  }, [commands, query]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const optionId = (index) => `${listId}-option-${index}`;

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    if (!open) return;
    document.getElementById(optionId(active))?.scrollIntoView?.({ block: 'nearest' });
  });

  const run = (command) => {
    if (!command) return;
    onClose();
    command.run?.();
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!flat.length) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((index) => (index + step + flat.length) % flat.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      run(flat[active]);
    }
  };

  let index = -1;
  return (
    <Dialog open={open} onClose={onClose} hideHeader ariaLabel="Palette de commandes" position="top" size="lg" initialFocusRef={input} panelClassName="overflow-hidden">
      <div className="flex items-center gap-3 border-b border-line px-4">
        <Search size={18} className="flex-shrink-0 text-fg-subtle" aria-hidden="true" />
        <input
          ref={input}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={flat.length ? optionId(active) : undefined}
          aria-label="Rechercher une page, une valeur ou une action"
          placeholder="Rechercher une page, une valeur, une action…"
          className="h-14 min-w-0 flex-1 bg-transparent text-[17px] text-fg placeholder:text-fg-subtle focus:outline-none"
        />
        <Kbd className="hidden sm:inline-flex">Échap</Kbd>
      </div>
      <div id={listId} role="listbox" aria-label="Résultats" className="max-h-[min(60vh,28rem)] overflow-y-auto p-2">
        {groups.map((group) => (
          <div key={group.label} role="group" aria-label={group.label}>
            <p aria-hidden="true" className="eyebrow px-3 pb-1.5 pt-3">{group.label}</p>
            {group.items.map((command) => {
              index += 1;
              const position = index;
              const selected = position === active;
              return (
                <div
                  key={command.id}
                  id={optionId(position)}
                  role="option"
                  aria-selected={selected}
                  onMouseMove={() => !selected && setActive(position)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => run(command)}
                  className={cx(
                    'relative flex cursor-pointer items-center gap-3 rounded-control px-3 py-2.5 transition-colors duration-100',
                    selected && 'bg-surface-3 before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full before:bg-gold-500',
                  )}
                >
                  {command.mark ? <SymbolMark symbol={command.mark} size="sm" /> : <IconTile icon={command.icon} size="sm" tone={selected ? 'gold' : 'neutral'} />}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-fg">{command.label}</span>
                    {command.hint && <span className="block truncate text-xs text-fg-subtle">{command.hint}</span>}
                  </span>
                  {selected && <CornerDownLeft size={14} className="flex-shrink-0 text-fg-subtle" aria-hidden="true" />}
                </div>
              );
            })}
          </div>
        ))}
        {!flat.length && <p className="px-3 py-10 text-center text-sm text-fg-muted">Aucun résultat pour « {query} »</p>}
      </div>
      <div className="hidden items-center gap-4 border-t border-line px-4 py-2.5 text-xs text-fg-subtle sm:flex" aria-hidden="true">
        <span className="flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> naviguer</span>
        <span className="flex items-center gap-1"><Kbd>↵</Kbd> ouvrir</span>
        <span className="flex items-center gap-1"><Kbd>Échap</Kbd> fermer</span>
      </div>
    </Dialog>
  );
};
